import React, { FunctionComponent, useContext, useMemo } from "react";
import { Button, Grid, makeStyles, Theme, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { ShopButtonIcon } from "../../assets/icons/shop-button-icon";
import { CardArrowPosition, CardWithArrow } from "../card-with-arrow/card-with-arrow";
import { Trans, useTranslation } from "react-i18next";
import { DARK_TEXT_COLOR } from "../../theme/theme";
import { getLocalizedLinks } from "../../translations/links";

const ICON_SIZE = 200;

interface IShopButtonStyleProps {
  mobileBubbleAnchor: CardArrowPosition;
  iconSize: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    position: "relative",
    "&:hover .card": {
      visibility: "visible",
      opacity: 1,
    },
  },
  button: {
    fontWeight: 700,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  heading: {
    fontWeight: 700,
    fontSize: 20,
    color: DARK_TEXT_COLOR,
    marginBottom: theme.spacing(2),
  },
  subheading: {
    fontWeight: 300,
    fontSize: 16,
    color: DARK_TEXT_COLOR,
    marginBottom: theme.spacing(4),
  },
  cardContainer: {
    position: "absolute",
    right: 0,
    top: 0,
    transform: "translate(0%, calc(-100% + 36px))",
    visibility: "hidden",
    opacity: 0,
    transition: "opacity 0.3s ease-in",
    width: "100%",
    minWidth: 400,
    maxWidth: 400,
    marginRight: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      transform: ({ mobileBubbleAnchor }: IShopButtonStyleProps) =>
        mobileBubbleAnchor === "start"
          ? "translate(0%, calc(-100% + 36px))"
          : mobileBubbleAnchor === "center"
          ? "translate(30%, calc(-100% + 36px))"
          : "translate(0%, calc(-100% + 36px))",
      width: "calc(100vw - 10px)",
      minWidth: "initial",
      maxWidth: 350,
    },
  },
  icon: {
    marginRight: -theme.spacing(5),
    width: ({ iconSize }) => iconSize,
    height: ({ iconSize }) => iconSize,
  },
  iconContainer: {
    width: ({ iconSize }) => iconSize,
    height: ({ iconSize }) => iconSize,
  },
  emphasized: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

interface IShopButton {
  mobileBubbleAnchor?: CardArrowPosition;
  iconSize?: number;
}

export const ShopButton: FunctionComponent<IShopButton> = (props) => {
  const iconSize = props.iconSize ? props.iconSize : ICON_SIZE;
  const mobileBubbleAnchor = props.mobileBubbleAnchor ? props.mobileBubbleAnchor : "end";
  const classes = useStyles({ mobileBubbleAnchor, iconSize });
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { isDemo } = useContext(NutritionContext);
  const productPageLink = useMemo(() => getLocalizedLinks(i18n).MY_DNA_PRODUCT_PAGE, [i18n]);
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      {isDemo && (
        <Grid container justify={"center"} className={classes.container}>
          <Grid item xs={12} className={`${classes.cardContainer} card`}>
            <CardWithArrow showArrow arrowDirection={"bottom"} arrowPosition={isSm ? mobileBubbleAnchor : "end"}>
              <Grid container direction={"column"}>
                <Typography className={classes.heading}>{t("shop_button.heading")}</Typography>
                <Typography className={classes.subheading}>
                  <Trans i18nKey="shop_button.content" components={[<span className={classes.emphasized} />]} />
                </Typography>
                <Button
                  className={classes.button}
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    window.open(productPageLink, "_blank");
                  }}
                >
                  {t("shop_button.button")}
                </Button>
              </Grid>
            </CardWithArrow>
          </Grid>
          <Grid container item justify={"flex-end"} className={classes.iconContainer}>
            <Grid item className={`${classes.icon} icon`}>
              <ShopButtonIcon></ShopButtonIcon>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};
